/**
 * Chart colour, decided by validator rather than by eye.
 *
 * The four Bigxstar hues do NOT survive as a categorical set. Run against the
 * light card surface they fail twice:
 *
 *   • Brand Yellow #FBBC05 sits at lightness 0.83 — outside the band — and
 *     reaches only 1.66:1 against white. The same reason it cannot carry text.
 *   • Darkening it to pass lightness then collides with Bigx Red: ΔE 3.7 under
 *     protanopia, 1.6 under deuteranopia. Dark yellow and red are the same
 *     colour to a red-green colourblind reader, and reordering does not help —
 *     it only moves which pair is adjacent.
 *
 * So the categorical set is THREE brand hues plus a neutral for the tail, which
 * passes every check in both modes. That is also the honest encoding: a till
 * takes cash, UPI and card, and everything else is a long tail nobody compares.
 *
 * Dark mode warns at ΔE 7.5 (deutan) — inside the floor band, legal only with
 * secondary encoding. Every chart here direct-labels its segments and leaves a
 * 2px surface gap between them, so identity is never colour alone.
 */

/**
 * A known tension, recorded rather than hidden: status hues are meant to be
 * reserved, and this brand has four colours of which three are already spoken
 * for — green is --success, yellow --warning, red --danger.
 *
 * Red is the one that actually misleads ("is that segment an error?"), so it is
 * not in the categorical set at all. Gold here is #926D00, a distinctly darker
 * step than --warning #FBBC05, so the two never appear as the same swatch.
 * Green is shared with --success, which is accepted: it appears only inside a
 * card titled "Payment methods", every segment is named in the legend beside
 * its value, and no status lives in that card to be confused with it.
 */

/** Categorical, in fixed order. Never cycled, never extended with a new hue. */
export const CAT = {
  blue: '#0078D9',   // Bigx Blue
  green: '#0F9D58',  // Bigx Green
  gold: '#926D00',   // Bigx Yellow, snapped to a passing step
} as const;

/** The tail. Neutral on purpose: "Other" is not a category you compare. */
export const CAT_OTHER = '#94A3B8';

/** Magnitude wants one hue, light to dark — not the categorical set. */
export const SEQ_HUE = CAT.blue;

/**
 * Payment methods, in fixed order so a filter can never repaint the survivors.
 * Anything not listed folds into "Other".
 */
export const PAYMENT_COLORS: Record<string, string> = {
  CASH: CAT.green,
  UPI: CAT.blue,
  CARD: CAT.gold,
};
